import { useEffect } from "react";

const SITE_URL = "https://tuneforus.ro";
const SITE_NAME = "TuneForUs";
const DEFAULT_DESCRIPTION =
  "TuneForUs creează melodii personalizate pentru cadouri, aniversări, nunți și momente speciale, cu versuri originale și livrare digitală.";

const upsertMeta = (selector, createTag, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = createTag();
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      tag.removeAttribute(key);
    } else {
      tag.setAttribute(key, value);
    }
  });
};

const cleanJsonLd = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(cleanJsonLd);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => [k, cleanJsonLd(v)])
    );
  }
  return value;
};

const SEO = ({
  title = "TuneForUs · Melodii personalizate pentru cadouri speciale",
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = `${SITE_URL}/uploads/site/hero-background.webp`,
  type = "website",
  jsonLd = [],
  noindex = false,
}) => {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`.replace(/\/$/, "") || SITE_URL;
    const canonicalUrl = canonical === SITE_URL ? `${SITE_URL}/` : canonical;

    document.title = title;
    document.documentElement.setAttribute("lang", "ro");

    upsertMeta('meta[name="description"]', () => {
      const tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      return tag;
    }, { content: description });

    upsertMeta('link[rel="canonical"]', () => {
      const tag = document.createElement("link");
      tag.setAttribute("rel", "canonical");
      return tag;
    }, { href: canonicalUrl });

    upsertMeta('meta[name="robots"]', () => {
      const tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      return tag;
    }, { content: noindex ? "noindex, nofollow" : "index, follow" });

    const metaTags = [
      ["property", "og:type", type],
      ["property", "og:site_name", SITE_NAME],
      ["property", "og:title", title],
      ["property", "og:description", description],
      ["property", "og:url", canonicalUrl],
      ["property", "og:image", image],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", title],
      ["name", "twitter:description", description],
      ["name", "twitter:image", image],
    ];

    metaTags.forEach(([attr, key, content]) => {
      upsertMeta(`meta[${attr}="${key}"]`, () => {
        const tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        return tag;
      }, { content });
    });

    document.head.querySelectorAll('script[type="application/ld+json"][data-tfu-seo="true"]').forEach((node) => node.remove());
    const graph = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
    graph.forEach((item) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-tfu-seo", "true");
      script.textContent = JSON.stringify(cleanJsonLd(item));
      document.head.appendChild(script);
    });
  }, [title, description, path, image, type, jsonLd, noindex]);

  return null;
};

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/uploads/site/favicon.png`,
  email: "contact@tuneforus.ro",
  sameAs: [SITE_URL],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: "ro-RO",
};

export const SITE = { url: SITE_URL, name: SITE_NAME, description: DEFAULT_DESCRIPTION };

export default SEO;
